import React, { useMemo, useCallback, useState } from 'react';
import { useInfiniteProducts, ProductsFilters } from '@/hooks/queries/useProducts';
import { Product } from '@/services/apis/products';
import ProductsGrid from './ProductsGrid';
import ProductsEmptyState from './ProductsEmptyState';
import ProductsErrorState from './ProductsErrorState';
import ToolModal from '@/components/ToolModal';

interface ProductsContentProps {
  filters: ProductsFilters;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
}

const ProductsContent: React.FC<ProductsContentProps> = ({ filters, hasActiveFilters, onClearFilters }) => {
  const [selectedTool, setSelectedTool] = useState<Product | null>(null);
  const [showToolModal, setShowToolModal] = useState(false);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error } = useInfiniteProducts(filters);

  const products = useMemo(() => {
    return (
      data?.pages.flatMap((page) => {
        return page.data || [];
      }) || []
    );
  }, [data]);

  const handleShowDetails = useCallback((product: Product) => {
    setSelectedTool(product);
    setShowToolModal(true);
  }, []);

  return (
    <>
      {/* Error State */}
      {error && (
        <ProductsErrorState error={error.message || 'An error occurred'} onRetry={() => window.location.reload()} />
      )}

      {/* Empty State */}
      {!isLoading && products.length === 0 && !error && (
        <ProductsEmptyState hasActiveFilters={hasActiveFilters} onClearFilters={onClearFilters} />
      )}

      {/* Products Grid */}
      {products.length > 0 && (
        <ProductsGrid
          products={products}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={!!hasNextPage}
          fetchNextPage={fetchNextPage}
          onShowDetails={handleShowDetails}
        />
      )}

      {/* Loading state when no products yet */}
      {isLoading && products.length === 0 && (
        <ProductsGrid
          products={[]}
          isLoading={true}
          isFetchingNextPage={false}
          hasNextPage={false}
          fetchNextPage={() => {}}
          onShowDetails={() => {}}
        />
      )}

      {/* Tool Modal */}
      {selectedTool && (
        <ToolModal
          product={selectedTool}
          isOpen={showToolModal}
          onClose={() => {
            setShowToolModal(false);
            setSelectedTool(null);
          }}
        />
      )}
    </>
  );
};

export default ProductsContent;

